import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Spinner } from "react-bootstrap";
import {
  FaCheck,
  FaClipboardList,
  FaEye,
  FaInfoCircle,
  FaRedoAlt,
  FaSignOutAlt,
  FaUsers
} from "react-icons/fa";
import {
  getClassroomTransitionReconciliation,
  reconcileClassroomTransitionProgressionPoint
} from "../services/TeacherAPIClient";
import { createWorkflowIdempotencyKey } from "../utils/teacherWorkflowRequests";

const normaliseRows = (value) => Array.isArray(value) ? value : [];

function TeacherClassroomReconciliation({
  progressionPoint,
  schoolId,
  targetAcademicYear,
  developmentMock = false,
  onBack,
  onCompleted
}) {
  const [state, setState] = useState(null);
  const [outcomes, setOutcomes] = useState({});
  const [destinations, setDestinations] = useState({});
  const [expandedClassrooms, setExpandedClassrooms] = useState([]);
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState("");
  const idempotencyKeyRef = useRef("");

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    setError("");
    setOutcomes({});
    setDestinations({});
    setExpandedClassrooms([]);
    idempotencyKeyRef.current = "";

    const request = developmentMock
      ? Promise.resolve({
          progressionPoint: {
            id: progressionPoint.id,
            sourceClassLevelName: progressionPoint.sourceClassLevelName,
            targetClassLevelName: progressionPoint.targetClassLevelName,
            reconciliationStatus: "pending",
            reconciliationVersion: progressionPoint.reconciliationVersion || 0
          },
          confirmedClassrooms: progressionPoint.confirmedClassrooms || [],
          leftoverChildren: progressionPoint.availableChildren || [],
          leftoverCount: Number(progressionPoint.availableChildCount || 0)
        })
      : getClassroomTransitionReconciliation({
          schoolId,
          targetAcademicYear,
          progressionPointId: progressionPoint.id
        });

    request
      .then((response) => {
        if (cancelled) return;
        if (!response?.progressionPoint || !Array.isArray(response?.confirmedClassrooms) ||
          !Array.isArray(response?.leftoverChildren)) {
          throw new Error("Malformed reconciliation response");
        }
        setState(response);
        setStatus("ready");
      })
      .catch((requestError) => {
        if (cancelled) return;
        setStatus("error");
        setError(
          requestError?.response?.status === 409
            ? "This class level is not ready for reconciliation. Close and reopen to load the latest progress."
            : "The children to reconcile could not be loaded. Close and try again."
        );
      });

    return () => {
      cancelled = true;
    };
  }, [developmentMock, progressionPoint, schoolId, targetAcademicYear]);

  const classrooms = normaliseRows(state?.confirmedClassrooms);
  const leftoverChildren = normaliseRows(state?.leftoverChildren);
  const accountedCount = leftoverChildren.filter((child) =>
    outcomes[child.id] && (outcomes[child.id] !== "assigned" || destinations[child.id])
  ).length;
  const unaccountedCount = leftoverChildren.length - accountedCount;
  const allAccountedFor = unaccountedCount === 0;
  const sourceClassLevelName = state?.progressionPoint?.sourceClassLevelName ||
    progressionPoint.sourceClassLevelName || "previous class level";
  const targetClassLevelName = state?.progressionPoint?.targetClassLevelName ||
    progressionPoint.targetClassLevelName || "class level";
  const outcomeOptions = [
    { value: "left_school", label: "Left the school", Icon: FaSignOutAlt },
    { value: "repeating", label: `Repeating ${sourceClassLevelName}`, Icon: FaRedoAlt },
    { value: "assigned", label: "Assign to classroom", Icon: FaUsers }
  ];
  const allExpanded = useMemo(
    () => classrooms.length > 0 && classrooms.every((classroom) =>
      expandedClassrooms.includes(classroom.id)
    ),
    [classrooms, expandedClassrooms]
  );

  const toggleClassroom = (classroomId) => {
    setExpandedClassrooms((current) => current.includes(classroomId)
      ? current.filter((id) => id !== classroomId)
      : [...current, classroomId]
    );
  };

  const handleComplete = async () => {
    if (!state || !allAccountedFor || status === "saving") return;
    setStatus("saving");
    setError("");
    const idempotencyKey = idempotencyKeyRef.current ||
      createWorkflowIdempotencyKey(`reconcile-${progressionPoint.id}`);
    idempotencyKeyRef.current = idempotencyKey;
    try {
      const payload = {
        schoolId,
        targetAcademicYear,
        progressionPointId: progressionPoint.id,
        idempotencyKey,
        version: Number(state.progressionPoint.reconciliationVersion || 0),
        outcomes: leftoverChildren.map((child) => ({
          studentId: child.id,
          outcome: outcomes[child.id],
          ...(outcomes[child.id] === "assigned"
            ? { destinationClassId: destinations[child.id] }
            : {})
        }))
      };
      const response = developmentMock
        ? {
            status: "completed",
            progressionPoint: {
              id: progressionPoint.id,
              reconciliationStatus: "completed",
              reconciliationVersion: payload.version + 1
            },
            allProgressionPointsReconciled: true
          }
        : await reconcileClassroomTransitionProgressionPoint(payload);
      onCompleted?.(response);
    } catch (requestError) {
      if (requestError?.response?.status === 409) {
        idempotencyKeyRef.current = "";
      }
      setStatus("ready");
      setError(
        requestError?.response?.status === 409
          ? "This reconciliation changed in another session. Close and reopen to load the latest outcomes."
          : requestError?.response?.status === 422
            ? "Account for every remaining child before completing this class level."
            : "The reconciliation could not be completed. No outcomes were applied. Please try again."
      );
    }
  };

  if (status === "loading") {
    return (
      <div className="classroom-transition-loading" role="status">
        <Spinner animation="border" size="sm" />
        <span>Loading confirmed classrooms and remaining children…</span>
      </div>
    );
  }

  if (status === "error") {
    return <Alert variant="danger">{error}</Alert>;
  }

  return (
    <div className="classroom-reconciliation">
      <section className="classroom-reconciliation-classrooms" aria-labelledby="confirmed-classrooms-heading">
        <div className="classroom-reconciliation-section-heading">
          <span className="classroom-reconciliation-heading-icon" aria-hidden="true">
            <FaClipboardList />
          </span>
          <div>
            <h3 id="confirmed-classrooms-heading">Classrooms for {targetClassLevelName}</h3>
            <p>All {classrooms.length} {targetClassLevelName} classrooms have been set up.</p>
          </div>
          <button
            type="button"
            className="classroom-reconciliation-view-all"
            onClick={() => setExpandedClassrooms(allExpanded ? [] : classrooms.map((item) => item.id))}
          >
            <FaEye aria-hidden="true" /> {allExpanded ? "Hide all children" : "View all children"}
          </button>
        </div>

        <div className="classroom-reconciliation-classroom-list">
          {classrooms.map((classroom) => {
            const expanded = expandedClassrooms.includes(classroom.id);
            return (
              <div className="classroom-reconciliation-classroom" key={classroom.id}>
                <div className="classroom-reconciliation-classroom-row">
                  <FaCheck className="classroom-reconciliation-check" aria-hidden="true" />
                  <strong>{classroom.name}</strong>
                  <span>{classroom.teacherName}</span>
                  <small>{classroom.teacherStatus === "matched" ? "Matched to existing teacher" : "Invitation pending"}</small>
                  <span>{Number(classroom.childCount || normaliseRows(classroom.children).length)} children</span>
                  <button type="button" onClick={() => toggleClassroom(classroom.id)}>
                    <FaEye aria-hidden="true" /> {expanded ? "Hide children" : "View children"}
                  </button>
                </div>
                {expanded && (
                  <ul aria-label={`${classroom.name} children`}>
                    {normaliseRows(classroom.children).map((child) => (
                      <li key={child.id}>{child.name}</li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <section className="classroom-reconciliation-leftovers" aria-labelledby="leftover-children-heading">
        <div className="classroom-reconciliation-section-heading">
          <span className="classroom-reconciliation-heading-icon warning" aria-hidden="true">
            <FaUsers />
          </span>
          <div>
            <h3 id="leftover-children-heading">
              {leftoverChildren.length === 0
                ? "All children have been added to this year’s classrooms"
                : `${leftoverChildren.length} ${leftoverChildren.length === 1 ? "child hasn’t" : "children haven’t"} been added to this year’s classrooms`}
            </h3>
            <p>
              {leftoverChildren.length === 0
                ? `There are no remaining children from last year’s ${sourceClassLevelName} cohort.`
                : "Choose what has happened to each child from last year."}
            </p>
          </div>
        </div>

        {leftoverChildren.length > 0 && (
          <div className="classroom-reconciliation-children">
            {leftoverChildren.map((child, index) => (
              <fieldset key={child.id}>
                <legend><span>{index + 1}</span> {child.name}</legend>
                <div className="classroom-reconciliation-outcomes">
                  {outcomeOptions.map(({ value, label, Icon }) => (
                    <label key={value} className={outcomes[child.id] === value ? "selected" : ""}>
                      <input
                        type="radio"
                        name={`reconciliation-${child.id}`}
                        value={value}
                        checked={outcomes[child.id] === value}
                        onChange={() => setOutcomes((current) => ({
                          ...current,
                          [child.id]: value
                        }))}
                      />
                      <Icon aria-hidden="true" /> {label}
                    </label>
                  ))}
                </div>
                {outcomes[child.id] === "assigned" && (
                  <select
                    className="classroom-reconciliation-destination"
                    aria-label={`Classroom for ${child.name}`}
                    value={destinations[child.id] || ""}
                    onChange={(event) => setDestinations((current) => ({
                      ...current,
                      [child.id]: event.target.value
                    }))}
                  >
                    <option value="">Choose a classroom</option>
                    {classrooms.map((classroom) => (
                      <option key={classroom.id} value={classroom.id}>{classroom.name}</option>
                    ))}
                  </select>
                )}
              </fieldset>
            ))}
          </div>
        )}

        <div className="classroom-reconciliation-gate" role="status">
          <span><FaInfoCircle aria-hidden="true" /> Once all children have been accounted for, you can complete the {targetClassLevelName} transition.</span>
          <strong>{unaccountedCount} of {leftoverChildren.length} children to account for</strong>
        </div>
      </section>

      {error && <Alert variant="danger" className="mb-0">{error}</Alert>}
      <div className="classroom-reconciliation-actions">
        <button type="button" className="classroom-reconciliation-back" onClick={onBack}>
          Back to previous
        </button>
        <button
          type="button"
          className="classroom-reconciliation-complete"
          disabled={!allAccountedFor || status === "saving"}
          onClick={handleComplete}
        >
          {status === "saving" ? <Spinner animation="border" size="sm" /> : <FaCheck aria-hidden="true" />}
          <span>
            <strong>Complete {targetClassLevelName} transition</strong>
            <small>You’ll return to Classroom Builder for the next class level</small>
          </span>
        </button>
      </div>
    </div>
  );
}

export default TeacherClassroomReconciliation;
